import React, { useState } from 'react';

/**
 * Forensic AI Triage Badge Component
 * Rules:
 *  - If AI >= threshold, display the percentage badge and signal details.
 *  - If AI < threshold, display absolutely nothing.
 *  - Candidates never see it (enforced via isAdmin prop and backend API guards).
 * 
 * Thresholds:
 *  - Historical: 85% (lenient to protect scanned prints & older digital cameras)
 *  - Present Neutral / Poses: 50%
 */
export default function AiBadge({ score, signals = [], stage = 'present', isAdmin = true }) {
  const [showTooltip, setShowTooltip] = useState(false);

  // Candidates NEVER see it
  if (!isAdmin) return null;

  // Determine stage-specific threshold
  const isHistorical = stage === 'Historical' || stage === 'old' || stage === 'historic';
  const threshold = isHistorical ? 85 : 50;

  const numScore = parseFloat(score);
  if (isNaN(numScore) || numScore < threshold) {
    return null; // Rule: Under threshold -> show ABSOLUTELY NOTHING
  }

  const roundedScore = Math.round(numScore);
  const isHighSeverity = roundedScore >= 75;

  const badgeStyle = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '9999px',
    fontSize: '0.72rem',
    fontWeight: 700,
    cursor: 'pointer',
    backgroundColor: isHighSeverity ? '#fee2e2' : '#fef3c7',
    color: isHighSeverity ? '#b91c1c' : '#b45309',
    border: `1px solid ${isHighSeverity ? '#fca5a5' : '#fde68a'}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    zIndex: 10,
    userSelect: 'none'
  };

  const tooltipStyle = {
    position: 'absolute',
    bottom: 'calc(100% + 6px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '0.72rem',
    lineHeight: '1.4',
    whiteSpace: 'normal',
    width: 'max-content',
    maxWidth: '260px',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)',
    border: '1px solid #334155',
    zIndex: 9999,
    pointerEvents: 'none'
  };

  const parsedSignals = Array.isArray(signals) ? signals : (typeof signals === 'string' ? [signals] : []);

  return (
    <div
      style={badgeStyle}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={(e) => {
        e.stopPropagation();
        setShowTooltip(!showTooltip);
      }}
      title={`AI Confidence: ${roundedScore}%`}
    >
      <span>🤖 AI: {roundedScore}%</span>
      <span style={{ fontSize: '0.68rem', opacity: 0.8 }}>ⓘ</span>

      {showTooltip && (
        <div style={tooltipStyle}>
          <div style={{ fontWeight: 800, color: isHighSeverity ? '#f87171' : '#fbbf24', marginBottom: '4px', borderBottom: '1px solid #334155', paddingBottom: '3px' }}>
            Forensic AI Signals ({roundedScore}%)
          </div>
          {parsedSignals.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: '14px' }}>
              {parsedSignals.map((sig, idx) => (
                <li key={idx} style={{ marginTop: '2px' }}>{sig}</li>
              ))}
            </ul>
          ) : (
            <div>Confidence score: {roundedScore}% (Threshold: {threshold}%)</div>
          )}
        </div>
      )}
    </div>
  );
}

